
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteMetaDefaults } from "@/components/RouteMetaDefaults";
import { BlogPageSkeleton } from "@/components/skeletons/BlogCardSkeleton";
import { ContentPageSkeleton } from "@/components/skeletons/ContentPageSkeleton";

// Use lazy loading for non-critical pages
const GetQuote = lazy(() => import("@/pages/GetQuote"));
const Blog = lazy(() => import("@/pages/Blog"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const BlogContentCalendar = lazy(() => import("@/pages/BlogContentCalendar"));
const FreeResources = lazy(() => import("@/pages/FreeResources"));
const MapCalibration = lazy(() => import("@/pages/MapCalibration"));

// Import route components directly instead of using fragments
import { featureRoutes } from "./FeatureRoutes";
import { productRoutes } from "./ProductRoutes";
import { legalRoutes } from "./LegalRoutes";
import { locationRoutes } from "./LocationRoutes";
import { blogRoutes } from "./BlogRoutes";

// Generic loading fallback with spinner
const LoadingFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
);

export const Routes = () => {
  return (
    <ErrorBoundary>
      <RouteMetaDefaults />
      <Suspense fallback={<ContentPageSkeleton />}>
        <RouterRoutes>
          {/* Core Routes */}
          <Route path="/" element={<Index />} />
          <Route path="/get-quote" element={
            <Suspense fallback={<LoadingFallback />}>
              <GetQuote />
            </Suspense>
          } />
          <Route path="/contact" element={
            <Suspense fallback={<LoadingFallback />}>
              <ContactUs />
            </Suspense>
          } />
          <Route path="/blog" element={
            <Suspense fallback={<BlogPageSkeleton />}>
              <Blog />
            </Suspense>
          } />
          <Route path="/blog-content-calendar" element={
            <Suspense fallback={<LoadingFallback />}>
              <BlogContentCalendar />
            </Suspense>
          } />
          {/* Legacy blog URL -> canonical /blog (mirrors the 301 in netlify.toml) */}
          <Route path="/blog-secondary-glazing" element={<Navigate to="/blog" replace />} />
          <Route path="/free-resources" element={
            <Suspense fallback={<LoadingFallback />}>
              <FreeResources />
            </Suspense>
          } />
          <Route path="/map-calibration" element={
            <Suspense fallback={<LoadingFallback />}>
              <MapCalibration />
            </Suspense>
          } />
          
          {/* Feature Routes - Spread the array of Route components */}
          {featureRoutes}
          
          {/* Product Routes - Spread the array of Route components */}
          {productRoutes}
          
          {/* Legal Routes - Spread the array of Route components */}
          {legalRoutes}
          
          {/* Location Routes - Spread the array of Route components */}
          {locationRoutes}
          
          {/* Blog Article Routes */}
          {blogRoutes}
          
          {/* 404 Not Found - Must be last */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </Suspense>
    </ErrorBoundary>
  );
};
